// React Imports
import React, { useState } from "react";

// 3rd Party Imports
import C from "classnames";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";

// Local Components
import { ScormExportDialog } from "../../ScormExportDialog/ScormExportDialog";
import S from "./LessonCard.module.scss";

// Hooks & Helpers
import { buildRoute } from "../../../../constants/routes";
import { useSmartNavigation } from "../../../../helpers/navigation";
import { isEmpty } from "../../../../utils";
import { useUserContext } from "../../../contexts";
import { getContentTagForDisplay, getVariantStyle } from "../../../utils";
import { buildLessonButtonText, getLessonBadgeSvg } from "../contentCardHelpers";

// Types
import { ContentCardProps } from "../contentCardTypes";

export const LessonCard: React.FC<ContentCardProps> = ({
  contentType = "video",
  variant,
  title,
  description,
  difficulty,
  estimatedTime,
  progress,
  status = "notStarted",
  lessonKey,
  roleId,
  roleEnrollmentId,
  disabled = false,
  required = true,
  catalogView = false,
  headingLevel: HeadingTag = "h4",
  inPathCard = false,
}) => {
  const { navigateTo } = useSmartNavigation();
  const { isTenantAdmin, isRestrictedBundle } = useUserContext();
  const [isScormDialogVisible, setIsScormDialogVisible] = useState(false);

  const navigateToContent = (): void => {
    // Carry the path context into the lesson so it can offer the next lesson (and enrollment)
    const pathQueryParams = {
      ...(roleId != null && { roleId: String(roleId) }),
      ...(roleEnrollmentId != null && { roleEnrollmentId: String(roleEnrollmentId) }),
    };
    if (contentType === "exercise") {
      navigateTo(
        buildRoute("Learn.routes.exercise.route", { "*": lessonKey || "" }, pathQueryParams)
      );
    } else {
      navigateTo(
        buildRoute("Learn.routes.lesson.route", { lessonKey: lessonKey || "" }, pathQueryParams)
      );
    }
  };

  const variantStyle = getVariantStyle(variant as string);

  const buttonText = buildLessonButtonText(contentType, status);
  const badgeSvg = getLessonBadgeSvg(contentType, status);

  const statusClass =
    status === "completed" ? S.lessonCompleted : status === "inProgress" ? S.lessonInProgress : "";

  // UI-only gate — real authorization must still be enforced by the SCORM export API.
  const shouldDisplayScormExport = isTenantAdmin && catalogView && !isRestrictedBundle;
  const safeId = lessonKey?.replace(/[^a-zA-Z0-9-_]/g, "-") || "unknown";

  return (
    <div className={C(S.lessonCard, statusClass, variantStyle, inPathCard && S.inPathCard)}>
      <Tooltip target={`#optional-icon-${safeId}`} content="Optional lesson" position="top" />
      <Tooltip target={`#export-scorm-${safeId}`} content="SCORM" position="top" />
      <div className={S.lessonCardHeader}>
        <Tag value={getContentTagForDisplay(contentType)} rounded={true} />
        <div className="ml-auto flex items-center gap-1">
          {!required && <i id={`optional-icon-${safeId}`} className="pi pi-lightbulb" />}
          {shouldDisplayScormExport && (
            <Button
              id={`export-scorm-${safeId}`}
              icon="pi pi-download"
              text
              rounded
              severity="contrast"
              aria-label="Export SCORM"
              className="p-0"
              onClick={() => setIsScormDialogVisible(true)}
            />
          )}
        </div>
      </div>
      <HeadingTag className={C("atlas-h4", "leading-none")}>{title}</HeadingTag>
      <p className={C("body-md", S.description, S.mutedText)}>{description}</p>
      <div className={S.lessonDetailsWrapper}>
        {difficulty && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Difficulty</p>
            <p className="body-md">{difficulty}</p>
          </div>
        )}
        {estimatedTime && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Estimated Time</p>
            <p className="body-md">~ {estimatedTime} mins</p>
          </div>
        )}
        {progress && !isEmpty(progress) && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Progress</p>
            <p className="body-md">
              {progress.completed}/{progress.totalLessons}
              Tasks
            </p>
          </div>
        )}
      </div>
      <div className={S.lessonCardFooter}>
        <Button label={buttonText} onClick={navigateToContent} disabled={disabled} />
      </div>
      {badgeSvg && (
        <div className={S.badgeSvgWrapper}>
          <img src={badgeSvg} alt={`${contentType} badge`} />
        </div>
      )}
      {shouldDisplayScormExport && isScormDialogVisible && (
        <ScormExportDialog
          visible
          onHide={() => setIsScormDialogVisible(false)}
          lessons={[
            { lessonKey: lessonKey || "", name: title || "", description: description || "" },
          ]}
        />
      )}
    </div>
  );
};
